<template lang="pug">
md-layout(md-column)
  // Page loader
  md-progress.md-primary(md-indeterminate, v-show='loadingComponent')
  // Page content
  md-theme(md-name='blue', v-show='!loadingComponent')
    // Title
    breadcrumbs#scrollToError(section='Video Sources', :nav='nav')

    // Form
    md-layout.form-wrapper(:md-gutter='16')
      md-layout(md-flex='100')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          metadata-form(title='Video Source', :entity='mediaSource', :form-submitted='formSubmitted', :validations='$v.mediaSource')
          status-radio(v-model='mediaSource.status')
      md-layout(md-flex='100')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          content-targets-form(:entity='mediaSource.targets', @created='showAlertToast($event, "success")', @error='showAlertToast($event, "error")', :form-submitted='formSubmitted', :validations='$v.mediaSource.targets')
          playlist-selector(:multiple='true', v-model='mediaSource.playlists').playlist-select


      md-layout(:md-gutter='16')
        md-layout(md-flex='50', md-flex-small='100')
          md-whiteframe.form-wrapper-frame(md-elevation='3')
            properties-form(:entity='mediaSource.setProperties', :form-submitted='formSubmitted', :validations='$v.mediaSource.setProperties')
        md-layout(md-flex='50', md-flex-small='100')
          md-whiteframe.form-wrapper-frame(md-elevation='3')
            ingest-settings-form(:entity='mediaSource.ingestSettings')
      md-layout(md-flex='100')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          feed-configuration-form(:entity='mediaSource', :form-submitted='formSubmitted', :validations='$v.mediaSource')
      md-layout.disclaimer-check(md-align='center', md-flex='50', md-flex-offset='25', v-show='!editMode')
        .md-caption I own and/or have the right to distribute this content across domains, geographic regions, platforms, and windows of time specified in accordance with the associated licenses attached to this content or perpetually, and without restriction in the absence of attached licenses.
        md-checkbox.md-primary.md-body-2(v-model='mediaSource.setProperties.accountHasRights') I own or can distribute this content.
      md-layout(md-flex='100', md-align='center')
        stitch-loader(:loading='creatingOrUpdating', :size='40')
        md-button#submit-btn.md-raised.md-primary(@click='onFormSubmit', v-show='!creatingOrUpdating', :disabled='$v.$pending || !mediaSource.setProperties.accountHasRights', md-theme='blue')
          | {{ editMode ? 'Update' : 'Create' }}
</template>

<script>
  import { validationMixin } from 'vuelidate';
  import { required, url } from 'vuelidate/lib/validators';
  import MetadataForm from '../../shared-components/forms/MetadataForm';
  import FeedConfigurationForm from '../../shared-components/forms/FeedConfigurationForm';
  import ContentTargetsForm from '../../shared-components/forms/ContentTargetsForm';
  import PropertiesForm from '../../shared-components/forms/PropertiesForm';
  import IngestSettingsForm from '../../shared-components/forms/IngestSettingsForm';
  import StatusRadio from '../../shared-components/StatusRadio';
  import StitchLoader from '../../shared-components/Loader';
  import mediaSourceService from '../../services/mediaSourceService';
  import Breadcrumbs from '../../shared-components/Breadcrumbs';
  import PlaylistSelector from "../../shared-components/playlist/PlaylistSelector.vue";


  export default {
    mixins: [validationMixin],
    components: {
      MetadataForm,
      StitchLoader,
      FeedConfigurationForm,
      ContentTargetsForm,
      PropertiesForm,
      IngestSettingsForm,
      StatusRadio,
      Breadcrumbs,
      PlaylistSelector
    },
    computed: {
      nav() {
        return [
          { title: 'Video Sources', route: 'VideoSources' },
          { title: this.editMode ? 'Edit Video Source' : 'New Video Source' }
        ]
      },
      editMode() {
        return !!this.$route.params.id;
      }
    },
    data() {
      return {
        mediaSourceOriginalName: null,
        loadingComponent: false,
        creatingOrUpdating: false,
        formSubmitted: false,
        mediaSource: {
          name: '',
          description: '',
          tags: [],
          status: 'draft',
          urls: [''],
          ingestType: 'mrss',
          targets: {
            licenses: []
          },
          playlists: [],
          setProperties: {
            demographic: {
              gender: null,
              households: [],
              incomes: [],
              ages: [],
              educations: []
            },
            categories: [],
            includeInMarketplace: false,
            accountHasRights: false
          },
          ingestSettings: {
            useStitchCDN: true,
            transcodeSourceFiles: true
          }
        }
      };
    },
    validations: {
      mediaSource: {
        name: {
          required,
          async isUnique(value) {
            const hasToCheck = !this.editMode || value !== this.mediaSourceOriginalName;
            if (value && hasToCheck) {
              const response = await mediaSourceService.getList({ exactName: value, limit: 1 });
              return response.data.total === 0;
            } else {
              return true;
            }
          }
        },
        shortDescription: {
          default:true
        },
        description : {
          default:true
        },
        urls: {
          $each: {
            required,
            url
          }
        },
        setProperties: {
          categories: {
            required
          }
        },
        targets: {
          licenses: {
            required(value) {
              return value.length > 0 || !this.mediaSource.setProperties.includeInMarketplace;
            }
          }
        }
      }
    },
    methods: {
      showAlertToast(text, icon){
        this.$swal({
              text: text,
              position: 'bottom-left',
              icon: icon,
              toast: true,
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              }
            )
      },

      updateMediaSource() {
        this.creatingOrUpdating = true;
        mediaSourceService.update(this.$route.params.id, this.mediaSource)
          .then(() => {
            this.showAlertToast('Video source updated successfully', 'success');
            this.$delayedNavigation(this.$router, 'VideoSources');
          })
          .catch(() => {
            this.showAlertToast('Video source could not be updated. Please, try again later.', 'error');
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },
      createMediaSource() {
        this.creatingOrUpdating = true;
        mediaSourceService.create(this.mediaSource)
          .then(() => {
            this.showAlertToast('Video source created successfully', 'success');
            this.$delayedNavigation(this.$router, 'VideoSources');
          })
          .catch((err) => {
            let message = "";
            
            if (err.status === 422) {
              message = err.errors
            } else {
              message = "Video source could not be created. Please, try again later.";
            }
            this.showAlertToast(message, 'error');
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },
      onFormSubmit() {
        this.formSubmitted = true;
        if (!this.$v.$invalid) {
          if (this.editMode) {
            this.updateMediaSource();
          } else {
            this.createMediaSource();
          }
        } else{
          const scrollToError = this.$el.querySelector("#scrollToError");
          scrollToError.scrollIntoView({ behavior: "smooth" })
        }
      },
    },
    mounted() {
      if (this.editMode) {
        this.loadingComponent = true;
        mediaSourceService.get(this.$route.params.id)
          .then((response) => {
            this.mediaSource = response.data.mediaSource;
            this.mediaSourceOriginalName = this.mediaSource.name;
          })
          .catch(() => {
            this.showAlertToast('Video source data could not be retrieved. Please, try again later', 'error');
          })
          .then(() => {
            this.loadingComponent = false;
          });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .form-wrapper {
    width: 100%;
    padding: 25px 15%;
    @media (max-width: 1200px) {
      padding: 10px .5%;
      margin-left: 0;
    }
    .md-layout {
      margin-bottom: 5px;
    }
  }
  .disclaimer-check {
    margin-top: 20px;
  }
  .form-wrapper-frame {
    width: 100%;
    padding: 25px;
    margin-bottom: 15px;
  }
  .title-icon {
    margin-top: -5px;
  }
  .form-divider {
    padding: 10px 0;
  }
  .playlist-select.input-container {
    padding-left: 12px;
    padding-right: 12px;
    max-width: 100%;
  }
  #submit-btn {
    margin-bottom: 50px;
    width: 33%;
    padding: 6px;
    font-size: 1.1em;
  }
</style>



// WEBPACK FOOTER //
// src/spa/mediaSource/MediaSourceForm.vue